import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { Box, Button, DialogContent, Grid2, Stack, Typography } from '@mui/material';

import { emailRegex, nameRegex } from 'constants/regex';
import { roundedComponentVariants } from 'constants/components';
import { getNameFormValue } from 'helper/getNameFormValue';
import { useDialog } from 'hooks/useDialog';
import { dialogPropsSelector } from 'store/entities/ui/slice';
import { RadioGroup } from 'components/complex/RadioGroup';
import { Input } from 'components/simple';
import { Dialog } from 'components/complex';

const NOMINEE_FIELDS = {
  FIRST_NAME: 'first_name',
  LAST_NAME: 'last_name',
  EMAIL: 'email',
  CONFIRM_EMAIL: 'confirmEmail',
  ROLE: 'role',
};

const formPrefix = 'edit-nominee-form';
const defaultValues = {
  [NOMINEE_FIELDS.FIRST_NAME]: '',
  [NOMINEE_FIELDS.LAST_NAME]: '',
  [NOMINEE_FIELDS.EMAIL]: '',
  [NOMINEE_FIELDS.CONFIRM_EMAIL]: '',
  [NOMINEE_FIELDS.ROLE]: '',
};

const styles = {
  formWrapper: ({ breakpoints }) => ({
    height: '100%',
    maxHeight: 550,
    overflowY: 'auto',
    [breakpoints.up('md')]: {
      overflowY: 'visible',
    },
  }),
  control: ({ palette }) => ({
    maxWidth: 355,
    '.MuiInputLabel-shrink': {
      color: `${palette.primary.main} !important`,
      fontFamily: 'Domine !important',
    },
  }),
  label: ({ typography }) => typography.caption,
};

const formInputs = [
  {
    name: NOMINEE_FIELDS.FIRST_NAME,
    rules: { required: true, pattern: nameRegex },
    label: 'First name',
    placeholder: 'e.g. Martin',
    isNameField: true,
  },
  {
    name: NOMINEE_FIELDS.LAST_NAME,
    rules: { required: true, pattern: nameRegex },
    label: 'Last name',
    placeholder: 'e.g. Sheen',
    isNameField: true,
  },
  {
    name: NOMINEE_FIELDS.EMAIL,
    rules: {
      required: true,
      pattern: {
        value: emailRegex,
        message: 'Current value is not a valid email',
      },
      deps: [NOMINEE_FIELDS.CONFIRM_EMAIL],
    },
    type: 'email',
    label: 'Email address',
    placeholder: 'e.g. martinsheen@monzo.com',
  },
  {
    name: NOMINEE_FIELDS.CONFIRM_EMAIL,
    rules: {
      required: true,
      validate: (value, { email }) => value === email || 'Email should match!',
    },
    type: 'email',
    label: 'Confirm email address',
    placeholder: 'e.g. martinsheen@monzo.com',
  },
];

export const NomineeModal = () => {
  const { roles, data, onSaveNominee } = useSelector(dialogPropsSelector);
  const { closeDialog } = useDialog();
  const {
    setValue,
    control,
    handleSubmit,
    formState: { isValid, isSubmitting },
  } = useForm({ defaultValues, mode: 'onChange' });

  useEffect(() => {
    Object.keys(data).forEach((key) => {
      setValue(key, data[key] || '');
    });

    setValue(NOMINEE_FIELDS.ROLE, roles[0]?.id ?? '');
  }, [data, roles, setValue]);

  return (
    <Dialog
      withCustomContent
      isLoading={isSubmitting}
      id="edit-feedback-framework-nominee-dialog"
      onClose={closeDialog}
      title={`${data.email ? 'Edit' : 'New'} feedback nominee`}
    >
      <DialogContent>
        <form onSubmit={handleSubmit(onSaveNominee)}>
          <Stack spacing={4}>
            <Box sx={styles.formWrapper}>
              <Grid2 container spacing={4}>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <Stack spacing={4}>
                    {formInputs.map(({ name, rules, isNameField, ...inputProps }) => (
                      <Controller
                        key={name}
                        name={name}
                        rules={rules}
                        control={control}
                        render={({ field: { ref, onChange, ...field }, fieldState: { error } }) => (
                          <Input
                            {...field}
                            {...inputProps}
                            id={`${formPrefix}-${name}`}
                            variant={roundedComponentVariants.squared}
                            inputProps={isNameField ? { maxLength: 30 } : {}}
                            onChange={(value) => (isNameField ? onChange(getNameFormValue(value)) : onChange(value))}
                            error={error}
                            innerRef={ref}
                            customStyles={{
                              control: styles.control,
                              label: styles.label,
                            }}
                          />
                        )}
                      />
                    ))}
                  </Stack>
                </Grid2>

                <Grid2 size={{ xs: 12, md: 6 }}>
                  <Controller
                    name="role"
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { ref, value, ...field } }) => (
                      <Stack spacing={4}>
                        <Typography variant="caption" color="primary">
                          What relationship does the nominee have to you?
                        </Typography>

                        <RadioGroup
                          {...field}
                          value={value}
                          innerRef={ref}
                          labelTypographyProps={{ variant: 'caption' }}
                          options={roles.map((role) => ({
                            id: `${NOMINEE_FIELDS.ROLE}_${role.id}`,
                            label: role.name,
                            value: role.id,
                          }))}
                        />
                      </Stack>
                    )}
                  />
                </Grid2>
              </Grid2>
            </Box>

            <Stack direction="row" spacing={2}>
              <Button color="secondary" onClick={closeDialog} disabled={isSubmitting}>
                Go Back
              </Button>

              <Button type="submit" disabled={!isValid} loading={isSubmitting}>
                {`${data.email ? 'Edit' : 'Save'} Nominee`}
              </Button>
            </Stack>
          </Stack>
        </form>
      </DialogContent>
    </Dialog>
  );
};
